import { isArray, isBoolean, isFunction, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { loadJS } from "../../../lib/utils/LoadJS.js";
import { encodeQS } from "../../../lib/utils/QueryStringUtils.js";
import { VideoInfoRawData } from "../../../model/video/VideoInfo.js";
import { VideoRef } from "../../player/MediaResource.js";
import { AdCallParams } from "../AdCallParams.js";
import { AdUnit, enrichAdUnitWithParams, isAdUnitWithParams } from "../AdUnit.js";
import { AdParamsEnricher } from "./PrebidModule.js";
import { AbstractPrebidModuleWithAdsManagerOps } from "./PrebidModuleWithAdsManagerOps.js";

const IASPET_URL = "https://cdn.adsafeprotected.com/iasPET.1.js";
const VANS_URL = "https://static.adsafeprotected.com/vans-adapter-google-ima.js";

export interface IASVideoSlot {

    adSlotId: string,
    size: [number,number],
    adUnitPath: string,
    type: "video"

}

type IASAdSlotRawData = string;


interface IASSlotParsedData  {
    fr: string,
    brandSafety: {[k: string]: string},
    slots: {[k: string]: {[k1: string]: string[] | string}}
}

function isIASSlotParsedData(x: any): x is IASSlotParsedData {

    return isObject(x) &&
            "fr" in x && isString(x.fr) &&
            "brandSafety" in x && isObject(x.brandSafety) &&
            "slots" in x && isObject(x.slots);
}

export interface IASPetRequest {
    adSlots: IASVideoSlot[],
    dataHandler: (data: IASAdSlotRawData) => void
}





interface IASPet {
    queue: {
        push(req: IASPetRequest): void
    },
    setTargetingForGPT(): void
}


function isIASPet(x: any): x is IASPet {

    return x && ("queue" in x) && 
        ("push" in x.queue && isFunction(x.queue.push));


}



export interface IASVansRequestData {
    anId: string,
    campId: string,
    chanId: string,
    placementId: 'Open Auction',
    pubOrder: 'Video',
    pubId: "Direct",
    custom?: string
    custom2?: string
}

export interface IASVansAdapter {
    init(g: typeof google, adsManager: google.ima.AdsManager, videoElement: HTMLVideoElement, reqData: IASVansRequestData): void
}


function isVansAdapter(x: any): x is IASVansAdapter {
    return x && "init" in x  && isFunction(x.init);
}

interface WindowWithIASPet {

    __iasPET: IASPet

};


function isWindowWithIASPet(x: any): x is WindowWithIASPet {
    return ("__iasPET" in x && isIASPet(x.__iasPET)); 
}


interface ExternalBidVariables {

    _fp_ias_enabled: boolean,
    _fp_ias_page_load?: boolean

}

function isExternalBidVariables(x: any): x is ExternalBidVariables {
    return (("_fp_ias_enabled" in x) && isBoolean(x._fp_ias_enabled)) &&
        x._fp_ias_enabled;
}


function loadPET(window: Window, pubId: string): Promise<IASPet> {

    if(isWindowWithIASPet(window)) {
        return Promise.resolve(window.__iasPET);
    }
    else {
        window["__iasPET"] = window["__iasPET"] || {};
        window["__iasPET"].queue = window["__iasPET"].queue || []; 
        window["__iasPET"].pubId = pubId;

        const iaspet: IASPet = window["__iasPET"];
        let script: HTMLScriptElement = window.document.head.querySelector("script[src='"+ IASPET_URL + "']");
                    
        if(!script) {
            script = window.document.createElement("script");
            script.src = IASPET_URL;
            script.async = true;
            window.document.head.appendChild(script);
        }
        return Promise.resolve(iaspet);
    }

}

interface WithVansAdapter {
    googleImaVansAdapter: IASVansAdapter
}

function isWithVansAdapter(x: any): x is WithVansAdapter {
    return "googleImaVansAdapter" in x && 
        isVansAdapter(x.googleImaVansAdapter);

}


export interface IASPrebidKit {
    pet: IASPet,
    vans: IASVansAdapter
}

function loadVANSAdapter(window: Window, pubId: string): Promise<IASPrebidKit> {
    return loadPET(window,pubId).then(pet => {
        if(isWithVansAdapter(window)) {
            return {
                pet: pet,
                vans: window.googleImaVansAdapter
            };
        }
        else {
            return loadJS(VANS_URL,window.document.head).then(x => {
                const vans: IASVansAdapter = window["googleImaVansAdapter"];
                return {
                    pet: pet,
                    vans: vans
                };
            });
        }
    });
}


//pubId = 930640
//anId = 931226

function requestSlotAndGetResponse(pet: IASPet, req: IASVideoSlot): Promise<IASSlotParsedData> {
    return new Promise((resolve,reject) =>{
  
        pet.queue.push({
            adSlots: [req],
            dataHandler: (data) => {
                try {
                    const json = JSON.parse(data);
                    if(isIASSlotParsedData(json)) {
                        resolve(json);
                    }
                    else {
                        console.error("Invalid ias data",json);
                        reject("invalid data in IAS response");
                    }
                }catch(e) {
                    console.error("[IAS]",e);
                    reject("Could not parse IAS rawdata");
                }
            }
        })
    }) 
}


export class IASHB extends AbstractPrebidModuleWithAdsManagerOps<IASPrebidKit> {


    constructor(private window: Window,
                private pubId: string, 
                private anId: string,
                private timeout: number = 1000) {
        super(timeout);
    }
   
 
    public get providerName(): string {
        return "IAS";
    }

    protected internalLoadOps(): Promise<IASPrebidKit> {
        return loadVANSAdapter(this.window,this.pubId);
    }
    
    protected internalBidOps(sdk: IASPrebidKit, adUnit: AdUnit, info: VideoInfoRawData): Promise<AdParamsEnricher> {
        const useUnit = enrichAdUnitWithParams(adUnit);
        if(isAdUnitWithParams(useUnit))  {

            return requestSlotAndGetResponse(sdk.pet,{
                adSlotId: "video",
                size: [1,1],
                type: "video",
                adUnitPath: useUnit.params.iu
            }).then(data => {
                return (incoming) => {

                    const obj1 = {
                        fr: data.fr,
                        ...data.brandSafety
                    };
                    const obj2 = {};
                    Object.entries(data.slots).forEach(x => {
                        const [slotId,slotValues]  = x;
                        
                        const serialized = Object.entries(slotValues).map(y => {
                            const [key,value] = y;
                            let uv = "";
                            if(isArray(value)) uv = value.join(",");
                            else uv = value
                            return key + "=" + uv;
                        }).join("&");

                        obj2[slotId] = serialized;
                    });
                    const targeting = encodeURIComponent(encodeQS(obj1,true) + ("video" in obj2?"&" + obj2["video"]:""));
                    const useParams: AdCallParams = incoming?incoming:{};
                    useParams.pre_serialized_custom_params = useParams.pre_serialized_custom_params || [];
                    useParams.pre_serialized_custom_params.push(targeting);
                    return useParams;
                }
            }).catch(err => {
                return Promise.reject(err);
            });
        }
        else return Promise.reject("Missing iu and sz params from adunit");
        
    }


    protected beforeAdManagerStartInternalOps<MPlayer>(sdk: IASPrebidKit, g: typeof google, adUnit: AdUnit, adsManager: google.ima.AdsManager, resource:VideoRef): Promise<void> {
        const useUnit = enrichAdUnitWithParams(adUnit);
        if(isAdUnitWithParams(useUnit))  {
            const config: IASVansRequestData = {
                anId: this.anId,
                campId: useUnit.params.sz,
                chanId: useUnit.params.iu,
                placementId: "Open Auction",
                pubOrder: "Video",
                pubId: "Direct",
                custom: this.window.location.hostname,
                custom2: resource.info.id
            }
            sdk.vans.init(g,adsManager,resource.element,config);
            return Promise.resolve();
        }
        else return Promise.reject("Missing iu and sz params from adunit");
        
    }
    
}